import {MessarioAiContextMessage} from '@lib/messarioAi/types';

export type MessarioAiHistoryMediaLike = {
  _?: string,
  document?: {
    _?: string,
    file_name?: string,
    mime_type?: string,
    attributes?: Array<{
      _?: string,
      voice?: boolean,
      round_message?: boolean,
      file_name?: string
    }>
  },
  photo?: {_?: string}
};

export type MessarioAiHistoryMessageLike = {
  _?: string,
  message?: string,
  pFlags?: {out?: true | boolean},
  media?: MessarioAiHistoryMediaLike | null,
  grouped_id?: string | number
};

const DEFAULT_HISTORY_LIMIT = 40;

type ResolvedMedia = {
  type: string,
  fileName?: string,
  mime?: string
};

/**
 * Build newest-last LLM context from history messages (already newest-first or mixed).
 * Filters service/empty rows. Synthesizes media markers (Web K album merge by grouped_id).
 * Upper bound is enforced by backend clamp (max_history_limit); no client hard cap of 16.
 */
export function buildMessarioAiContextFromMessages(
  messagesNewestFirst: MessarioAiHistoryMessageLike[],
  historyLimit: number
): MessarioAiContextMessage[] {
  const parsed = Math.floor(Number(historyLimit));
  const limit = Math.max(1, Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_HISTORY_LIMIT);
  const collected: MessarioAiContextMessage[] = [];
  const seenGroupedIds = new Set<string>();

  for(const message of messagesNewestFirst) {
    if(!message || message._ !== 'message') {
      continue;
    }

    const groupedKey = groupedIdKey(message.grouped_id);
    if(groupedKey) {
      if(seenGroupedIds.has(groupedKey)) {
        continue;
      }
      seenGroupedIds.add(groupedKey);

      const album = messagesNewestFirst.filter((row) => (
        row &&
        row._ === 'message' &&
        groupedIdKey(row.grouped_id) === groupedKey
      ));
      const mapped = mapAlbumMessages(album);
      if(!mapped) {
        continue;
      }
      collected.push(mapped);
    } else {
      const mapped = mapSingleMessage(message);
      if(!mapped) {
        continue;
      }
      collected.push(mapped);
    }

    if(collected.length >= limit) {
      break;
    }
  }

  // Newest-last for the API.
  return collected.reverse();
}

function mapSingleMessage(message: MessarioAiHistoryMessageLike): MessarioAiContextMessage | undefined {
  const actor = message.pFlags?.out ? 'Менеджер' : 'Клиент';
  const media = resolveMedia(message.media);
  const marker = media ? buildMediaMarker(actor, media, 1) : undefined;
  const caption = normalizeCaption(message.message);
  const content = combineMarkerAndCaption(marker, caption);
  if(!content) {
    return undefined;
  }

  return {
    role: message.pFlags?.out ? 'assistant' : 'user',
    content
  };
}

function mapAlbumMessages(album: MessarioAiHistoryMessageLike[]): MessarioAiContextMessage | undefined {
  if(!album.length) {
    return undefined;
  }

  const outgoing = !!album[0]?.pFlags?.out;
  const actor = outgoing ? 'Менеджер' : 'Клиент';
  const medias = album.map((row) => resolveMedia(row.media)).filter((row): row is ResolvedMedia => !!row);
  const photoCount = medias.filter((row) => row.type === 'photo').length;
  const allPhotos = medias.length > 0 && photoCount === medias.length;

  let marker: string | undefined;
  if(allPhotos) {
    marker = buildMediaMarker(actor, {type: 'photo'}, Math.max(1, photoCount));
  } else if(medias.length === 1) {
    marker = buildMediaMarker(actor, medias[0], 1);
  } else if(medias.length > 1) {
    // Mixed / non-photo album: keep a short aggregate marker.
    marker = `[${actor} отправил ${medias.length} файл${russianFileSuffix(medias.length)}]`;
  }

  let caption: string | undefined;
  for(const row of album) {
    const next = normalizeCaption(row.message);
    if(next) {
      caption = next;
      break;
    }
  }

  const content = combineMarkerAndCaption(marker, caption);
  if(!content) {
    return undefined;
  }

  return {
    role: outgoing ? 'assistant' : 'user',
    content
  };
}

function resolveMedia(media: MessarioAiHistoryMediaLike | null | undefined): ResolvedMedia | undefined {
  if(!media || typeof media._ !== 'string') {
    return undefined;
  }

  if(media._ === 'messageMediaPhoto') {
    return {type: 'photo'};
  }

  if(media._ === 'messageMediaDocument') {
    const doc = media.document;
    const attrs = Array.isArray(doc?.attributes) ? doc.attributes : [];
    const fileName =
      (typeof doc?.file_name === 'string' && doc.file_name.trim()) ||
      attrs.find((attr) => attr?._ === 'documentAttributeFilename' && typeof attr.file_name === 'string')?.file_name ||
      undefined;
    const mime = typeof doc?.mime_type === 'string' ? doc.mime_type : undefined;

    if(attrs.some((attr) => attr?._ === 'documentAttributeSticker')) {
      return {type: 'sticker', fileName, mime};
    }
    if(attrs.some((attr) => attr?._ === 'documentAttributeAnimated')) {
      return {type: 'animation', fileName, mime};
    }
    const videoAttr = attrs.find((attr) => attr?._ === 'documentAttributeVideo');
    if(videoAttr) {
      return {
        type: videoAttr.round_message ? 'video_note' : 'video',
        fileName,
        mime
      };
    }
    const audioAttr = attrs.find((attr) => attr?._ === 'documentAttributeAudio');
    if(audioAttr) {
      return {
        type: audioAttr.voice ? 'voice' : 'audio',
        fileName,
        mime
      };
    }
    if(mime && mime.toLowerCase().startsWith('image/')) {
      return {type: 'photo', fileName, mime};
    }
    if(looksLikeDocumentMime(mime)) {
      return {type: 'document', fileName, mime};
    }

    return {type: 'file', fileName, mime};
  }

  if(media._ === 'messageMediaWebPage') {
    return {type: 'webpage'};
  }
  if(media._ === 'messageMediaContact') {
    return {type: 'contact'};
  }

  return {type: 'file'};
}

function buildMediaMarker(actor: string, media: ResolvedMedia, imageCount: number): string {
  const label = mediaTypeLabel(media, imageCount);
  return `[${actor} отправил ${label}]`;
}

function mediaTypeLabel(media: ResolvedMedia, imageCount: number): string {
  const type = media.type;
  const name = (media.fileName || '').trim();

  switch(type) {
    case 'photo':
      return imageCountLabel(imageCount);
    case 'video':
      return 'видео';
    case 'video_note':
      return 'видеосообщение';
    case 'voice':
      return 'голосовое';
    case 'audio':
      return 'аудио';
    case 'sticker':
      return 'стикер';
    case 'animation':
      return 'GIF';
    case 'document':
      return namedAttachmentLabel('документ', name);
    case 'file':
      return namedAttachmentLabel('файл', name);
    case 'webpage':
      return 'ссылку';
    case 'contact':
      return 'контакт';
    default:
      return namedAttachmentLabel('файл', name);
  }
}

function imageCountLabel(count: number): string {
  const n = Math.max(1, count);
  if(n === 1) {
    return 'изображение';
  }
  return `${n} ${russianImageNoun(n)}`;
}

function russianImageNoun(n: number): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if(n10 === 1 && n100 !== 11) {
    return 'изображение';
  }
  if(n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) {
    return 'изображения';
  }
  return 'изображений';
}

function russianFileSuffix(n: number): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if(n10 === 1 && n100 !== 11) {
    return '';
  }
  if(n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) {
    return 'а';
  }
  return 'ов';
}

function namedAttachmentLabel(kind: string, name: string): string {
  if(!name) {
    return kind;
  }
  return `${kind}: ${name}`;
}

function looksLikeDocumentMime(mime: string | undefined): boolean {
  if(!mime) {
    return false;
  }
  const lower = mime.toLowerCase();
  if(lower.startsWith('application/pdf')) {
    return true;
  }
  return lower.includes('msword') ||
    lower.includes('officedocument') ||
    lower.includes('opendocument') ||
    lower === 'text/plain' ||
    lower === 'application/rtf';
}

function combineMarkerAndCaption(marker: string | undefined, caption: string | undefined): string | undefined {
  if(marker && caption) {
    return `${marker}\n${caption}`;
  }
  if(marker) {
    return marker;
  }
  return caption;
}

function groupedIdKey(value: unknown): string | undefined {
  if(value === undefined || value === null || value === '') {
    return undefined;
  }
  return String(value);
}

function normalizeCaption(value: unknown): string | undefined {
  if(typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if(!trimmed) {
    return undefined;
  }

  if(isServiceJunk(trimmed)) {
    return undefined;
  }

  return trimmed;
}

function isMediaMarkerContent(text: string): boolean {
  return text.startsWith('[Клиент ') || text.startsWith('[Менеджер ');
}

function isServiceJunk(text: string): boolean {
  if(isMediaMarkerContent(text)) {
    return false;
  }

  const lower = text.toLowerCase();
  const junk = [
    '[service]',
    '[system]',
    'message service',
    'chat created',
    'group created',
    'channel created'
  ];
  return junk.some((marker) => lower.includes(marker));
}
